import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import DealCard from '../components/DealCard';
import { 
  Plus, Search, Home, Bell, User, Sparkles,
  Target, Crown, ChevronRight, Trophy, Flame
} from 'lucide-react';

// Grabbit Logo URL
const GRABBIT_LOGO = "https://customer-assets.emergentagent.com/job_8dd61eda-1b76-4ded-9514-b53b1f23bf59/artifacts/uppz6d01_Grabbit.png";

// Category configuration with colors and vendors
const CATEGORIES = [
  { id: 'beauty', name: 'Beauty', icon: '💄', color: '#F9A8D4', bgColor: 'bg-pink-200/20' },
  { id: 'clothing', name: 'Clothing', icon: '👕', color: '#7DD3FC', bgColor: 'bg-sky-200/20' },
  { id: 'shoes', name: 'Shoes', icon: '👟', color: '#4ADE80', bgColor: 'bg-green-200/20' },
  { id: 'home', name: 'Home', icon: '🪑', color: '#FCD34D', bgColor: 'bg-amber-200/20' },
  { id: 'tech', name: 'Tech', icon: '📱', color: '#A78BFA', bgColor: 'bg-violet-200/20' },
  { id: 'travel', name: 'Travel', icon: '✈️', color: '#2DD4BF', bgColor: 'bg-teal-200/20' },
  { id: 'gifts', name: 'Gifts', icon: '🎁', color: '#FB923C', bgColor: 'bg-orange-200/20' },
  { id: 'flash', name: 'Flash Deals', icon: '⚡', color: '#F87171', bgColor: 'bg-red-200/20' },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [deals, setDeals] = useState([]);
  const [watches, setWatches] = useState([]);
  const [experts, setExperts] = useState([]);
  const [grabbitWins, setGrabbitWins] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [dealsRes, watchesRes, expertsRes, notificationsRes, statusRes, winsRes] = await Promise.all([
        api.get(`/deals/drops?limit=20${selectedCategory ? `&category=${selectedCategory}` : ''}`),
        api.get('/watches'),
        api.get('/experts?limit=6'),
        api.get('/notifications?limit=5'),
        api.get('/my-status'),
        api.get('/grabbit-wins?limit=5'),
      ]);
      
      setDeals(dealsRes.data);
      setWatches(watchesRes.data);
      setExperts(expertsRes.data);
      setNotifications(notificationsRes.data);
      setUserStatus(statusRes.data);
      setGrabbitWins(winsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveDeal = async (dealId, isSaved) => {
    try {
      if (isSaved) {
        await api.delete(`/deals/drops/${dealId}/save`);
      } else {
        await api.post(`/deals/drops/${dealId}/save`);
      }
      setDeals(deals.map(d => 
        d.drop_id === dealId 
          ? { ...d, is_saved: !isSaved, saves_count: d.saves_count + (isSaved ? -1 : 1) }
          : d
      ));
      toast.success(isSaved ? 'Removed from saved' : 'Deal grabbed! 🐰');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleWatchDeal = async (dealId) => {
    try {
      await api.post(`/deals/drops/${dealId}/watch`);
    } catch (error) {
      // Silent
    }
  };

  const unreadNotifications = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Left: Grabbit Logo + Status */}
            <div className="flex items-center gap-3">
              <img 
                src={GRABBIT_LOGO} 
                alt="Grabbit" 
                className="h-10 w-auto"
              />
              {userStatus && (
                <span 
                  className="px-2 py-0.5 rounded-full text-xs font-medium hidden sm:inline-flex items-center gap-1"
                  style={{ backgroundColor: `${userStatus.color}20`, color: userStatus.color }}
                >
                  {userStatus.icon} {userStatus.name}
                </span>
              )}
            </div>

            {/* Center: Search */}
            <div className="flex-1 max-w-md mx-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search deals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-full bg-muted/50 border-none focus:ring-2 focus:ring-primary text-sm"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Right: Notifications */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/notifications')}
              className="relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {unreadNotifications}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        
        {/* ============= SECTION 1: Your Active Deal Watches ============= */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-lg font-bold">Your Active Deal Watches</h2>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/create-watch')}
              className="text-primary"
              data-testid="create-watch-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Watch
            </Button>
          </div>

          {watches.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6 text-center border border-dashed border-primary/30"
            >
              <div className="text-4xl mb-3">🐰</div>
              <h3 className="font-semibold mb-1">Tell us what you want to grab</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Set up a Deal Watch and we'll hunt the best deals for you
              </p>
              <Button 
                onClick={() => navigate('/create-watch')}
                className="rounded-full bg-gradient-to-r from-pink-500 to-purple-600"
                data-testid="first-watch-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start My First Watch
              </Button>
            </motion.div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {watches.map((watch, index) => {
                const cat = CATEGORIES.find(c => c.id === watch.category);
                const progressToTarget = watch.best_current_discount 
                  ? Math.min((watch.best_current_discount / watch.discount_threshold) * 100, 100)
                  : 0;
                
                return (
                  <motion.div
                    key={watch.watch_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => navigate(`/watch/${watch.watch_id}`)}
                    className="min-w-[300px] glass rounded-2xl p-4 cursor-pointer hover:border-primary/50 transition-all"
                    style={{ borderColor: `${cat?.color}40` }}
                    data-testid={`watch-${watch.watch_id}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{cat?.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold">{watch.product_type}</h4>
                        <p className="text-xs text-muted-foreground">
                          {watch.brands?.length > 0 ? watch.brands.join(', ') : 'Any brand'}
                          {watch.vendors?.length > 0 && ` • ${watch.vendors.slice(0,2).join(', ')}`}
                        </p>
                      </div>
                      {watch.matches_count > 0 && (
                        <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-500 text-xs font-medium">
                          {watch.matches_count} match{watch.matches_count > 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>

                    {/* Progress to target */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Target: {watch.discount_threshold}% off</span>
                        {watch.best_current_discount && (
                          <span style={{ color: cat?.color }}>
                            Best: {watch.best_current_discount}% off
                            {watch.discount_threshold >= 50 && " 🔥"}
                          </span>
                        )}
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full rounded-full"
                          style={{ backgroundColor: cat?.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${progressToTarget}%` }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        {watch.price_cap && <span>Max: ${watch.price_cap}</span>}
                        {watch.stretch_budget && <span>Stretch: ${watch.stretch_budget}</span>}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
              
              {/* Add new watch card */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => navigate('/create-watch')}
                className="min-w-[120px] glass rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-all border-dashed"
              >
                <Plus className="w-8 h-8 text-muted-foreground mb-2" />
                <span className="text-xs text-muted-foreground">Add Watch</span>
              </motion.div>
            </div>
          )}
        </section>

        {/* ============= SECTION 2: Experts You Should Follow ============= */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              <h2 className="font-heading text-lg font-bold">Experts to Follow</h2>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/experts')}
              className="text-muted-foreground"
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {experts.map((expert, index) => (
              <motion.div
                key={expert.user_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/locker/${expert.user_id}`)}
                className="glass rounded-2xl p-4 text-center cursor-pointer hover:border-primary/50 transition-all group"
                data-testid={`expert-${expert.user_id}`}
              >
                {/* Avatar */}
                <div className="relative mx-auto w-14 h-14 mb-3">
                  <div 
                    className="w-full h-full rounded-full p-0.5"
                    style={{ background: `linear-gradient(135deg, #F472B6, #8B5CF6)` }}
                  >
                    <div className="w-full h-full rounded-full bg-background overflow-hidden flex items-center justify-center">
                      {expert.avatar_url ? (
                        <img src={expert.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-bold">{expert.name?.charAt(0)}</span>
                      )}
                    </div>
                  </div>
                  {expert.is_pro && (
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-xs">
                      🏆
                    </span>
                  )}
                </div>

                <h4 className="font-semibold text-sm truncate">{expert.name}</h4>
                <p className="text-xs text-muted-foreground mb-2 truncate">
                  {expert.specializations?.slice(0, 2).join(', ') || 'Deal Expert'}
                </p>
                
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-3">
                  <span>{expert.deals_posted} deals</span>
                  <span>•</span>
                  <span>{expert.success_rate}%</span>
                </div>

                <p className="text-xs text-green-500 mb-3">{expert.recent_activity}</p>

                <Button 
                  size="sm" 
                  className="w-full rounded-full text-xs h-8 group-hover:bg-gradient-to-r group-hover:from-pink-500 group-hover:to-purple-600 group-hover:text-white"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.success(`Following ${expert.name}! 🐰`);
                  }}
                  data-testid={`follow-${expert.user_id}`}
                >
                  Follow
                </Button>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ============= SECTION 3: Grabbit Wins 🏆 ============= */}
        {grabbitWins.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <h2 className="font-heading text-lg font-bold">Grabbit Wins</h2>
              </div>
            </div>

            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {grabbitWins.map((win, index) => {
                const cat = CATEGORIES.find(c => c.id === win.category);
                return (
                  <motion.div
                    key={win.purchase_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="min-w-[200px] glass rounded-2xl p-3 text-center"
                  >
                    <div className="relative mx-auto w-12 h-12 mb-2">
                      <div className="w-full h-full rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 flex items-center justify-center overflow-hidden">
                        {win.user_avatar ? (
                          <img src={win.user_avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg">{win.user_name?.charAt(0)}</span>
                        )}
                      </div>
                      <span className="absolute -bottom-1 -right-1 text-lg">{win.win_type === 'steal_mode' ? '🔥' : '💰'}</span>
                    </div>
                    <p className="font-semibold text-sm truncate">{win.user_name}</p>
                    <p className="text-xs text-muted-foreground">{win.user_status_icon} {win.user_status}</p>
                    <div className="mt-2 p-2 rounded-lg bg-green-500/10">
                      <span className="text-green-500 font-bold text-lg">
                        {win.discount_percent >= 50 ? '🔥' : ''} -{win.discount_percent}%
                      </span>
                      <p className="text-xs text-muted-foreground truncate">{win.product_name}</p>
                      {win.savings > 0 && (
                        <p className="text-xs text-green-500">Saved ${win.savings.toFixed(0)}</p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* ============= SECTION 4: Trending Deals by Category ============= */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-500" />
              <h2 className="font-heading text-lg font-bold">Trending Deals</h2>
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                !selectedCategory 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
              data-testid="category-all"
            >
              <Sparkles className="w-4 h-4" />
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  selectedCategory === cat.id 
                    ? 'text-black shadow-md' 
                    : 'bg-muted hover:bg-muted/80'
                }`}
                style={selectedCategory === cat.id ? { backgroundColor: cat.color } : {}}
                data-testid={`category-${cat.id}`}
              >
                <span>{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Deal Grid */}
          {deals.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-3xl p-12 text-center"
            >
              <div className="text-6xl mb-4">🐰</div>
              <h3 className="font-heading text-xl font-semibold mb-2">No deals yet</h3>
              <p className="text-muted-foreground mb-6">
                Be the first to drop a deal in this category!
              </p>
              <Button 
                onClick={() => navigate('/drop-deal')}
                className="rounded-full bg-gradient-to-r from-pink-500 to-purple-600"
                data-testid="first-drop-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Drop a Deal
              </Button>
            </motion.div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence>
                {deals.map((deal, index) => (
                  <motion.div
                    key={deal.drop_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <DealCard 
                      deal={deal}
                      category={CATEGORIES.find(c => c.id === deal.category)}
                      onSave={() => handleSaveDeal(deal.drop_id, deal.is_saved)}
                      onWatch={() => handleWatchDeal(deal.drop_id)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </section>
      </main>

      {/* Floating Drop Button - Grabbit style */}
      <motion.button
        onClick={() => navigate('/drop-deal')}
        className="fixed bottom-24 right-6 w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 shadow-lg shadow-pink-500/30 flex items-center justify-center z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{ 
          boxShadow: ['0 0 20px rgba(244,114,182,0.3)', '0 0 40px rgba(244,114,182,0.5)', '0 0 20px rgba(244,114,182,0.3)']
        }}
        transition={{ boxShadow: { duration: 2, repeat: Infinity } }}
        data-testid="drop-deal-fab"
      >
        <Plus className="w-6 h-6 text-white" />
      </motion.button>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 glass border-t border-border/50 px-4 py-2 z-40">
        <div className="max-w-md mx-auto flex items-center justify-around">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="text-primary"
            data-testid="nav-home"
          >
            <Home className="w-6 h-6" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/create-watch')}
            data-testid="nav-watch"
          >
            <Target className="w-6 h-6" />
          </Button>
          <div className="w-14" /> {/* Spacer for FAB */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/notifications')}
            className="relative"
            data-testid="nav-notifications"
          >
            <Bell className="w-6 h-6" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadNotifications}
              </span>
            )}
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/locker/${user?.user_id}`)}
            data-testid="nav-locker"
          >
            <User className="w-6 h-6" />
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
